/**
 * AsyncButton Component
 *
 * A wrapper around Fluent UI Button that handles async operation loading states.
 * Follows Microsoft's official Fluent UI pattern for button loading states:
 * - Uses Spinner in icon slot when loading (since Button doesn't have built-in loading prop)
 * - Uses disabledFocusable instead of disabled for better accessibility contrast
 * - Shows optional loading text to communicate state to users
 *
 * @see https://github.com/microsoft/fluentui/issues/21085
 */

import { Button, Spinner } from '@fluentui/react-components';
import type { ButtonProps } from '@fluentui/react-components';

export interface AsyncButtonProps extends Omit<ButtonProps, 'disabledFocusable'> {
  /** When true, shows loading spinner and prevents interactions */
  loading?: boolean;
  /** Text to display while loading (replaces children) */
  loadingText?: string;
}

export function AsyncButton(props: AsyncButtonProps): JSX.Element {
  const { loading = false, loadingText, icon, children, onClick, ...rest } = props;
  const handleClick = loading ? undefined : onClick;
  const displayIcon = loading ? <Spinner size="tiny" /> : icon;
  const displayText = loading && loadingText ? loadingText : children;

  // Cast to avoid complex union type issues with ButtonProps
  const buttonProps = {
    ...rest,
    icon: displayIcon,
    disabledFocusable: loading,
    onClick: handleClick,
  } as ButtonProps;

  return <Button {...buttonProps}>{displayText}</Button>;
}
